// Business Entity overview page — tabs for internal fees and internal revenues.
// Routes: /pricing/be/details/:id  and  /pricing/be/details/:id/:tab
import { useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useBusinessEntityDetailsQuery,
  useBusinessEntityOperationsQuery,
} from './api';
import type { BEOperationRow, BEOperationsResult } from './api';
import { OperationActionEnum, OperationGroupEnum, PricingTypeEnum } from '@/types/pricing';

const TABS = ['internal-fees', 'internal-revenues'] as const;
type TabKey = (typeof TABS)[number];

const TAB_GROUPS: Record<TabKey, OperationGroupEnum> = {
  'internal-fees': OperationGroupEnum.INTERNAL_FEE,
  'internal-revenues': OperationGroupEnum.INTERNAL_REVENUE,
};

export function BusinessEntityOverviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id, tab } = useParams<{ id: string; tab?: string }>();

  const beId = Number(id);
  const selectedTab: TabKey = (TABS.includes(tab as TabKey) ? tab : TABS[0]) as TabKey;
  const selectedTabIdx = TABS.indexOf(selectedTab);

  const detailsQuery = useBusinessEntityDetailsQuery(beId, !isNaN(beId));
  const opsQuery = useBusinessEntityOperationsQuery(beId, !isNaN(beId));

  // The API returns the "unknown" cast; shape it locally.
  const ops = opsQuery.data as BEOperationsResult | undefined;
  const feeOps: BEOperationRow[] = ops?.operationWithInternalFees ?? [];
  const revenueOps: BEOperationRow[] = ops?.operationWithInternalRevenues ?? [];

  const be = detailsQuery.data as { id: number; companyName?: string; subscriptionTier?: string } | undefined;

  const customFeeCount = useMemo(
    () => feeOps.filter((o) => o.pricingType?.toLowerCase() === PricingTypeEnum.CUSTOM).length,
    [feeOps],
  );
  const customRevenueCount = useMemo(
    () => revenueOps.filter((o) => o.pricingType?.toLowerCase() === PricingTypeEnum.CUSTOM).length,
    [revenueOps],
  );

  const isLoading = detailsQuery.isLoading || opsQuery.isLoading;

  const handleTabChange = (_: React.SyntheticEvent, idx: number) => {
    const newTab = TABS[idx];
    navigate(`/pricing/be/details/${id}/${newTab}`, { replace: true });
  };

  const openDetails = (
    action: OperationActionEnum,
    operationGroup: OperationGroupEnum,
    operationType: string,
  ) => {
    navigate(
      `/pricing/be/details/${id}/${action}/${operationGroup}/${operationType.toLowerCase()}`,
    );
  };

  if (!isNaN(beId) === false) {
    navigate('/pricing/be', { replace: true });
    return null;
  }

  const pricingChip = (pricingType: string) => {
    const isCustom = pricingType?.toLowerCase() === PricingTypeEnum.CUSTOM;
    return (
      <Chip
        size="small"
        color={isCustom ? 'warning' : 'default'}
        label={t(
          `pages.pricing.be_pricing.table.labels.${pricingType?.toUpperCase() ?? 'DEFAULT'}`,
          { defaultValue: pricingType ?? 'DEFAULT' },
        )}
      />
    );
  };

  const renderOpsTable = (
    rows: BEOperationRow[],
    operationGroup: OperationGroupEnum,
  ) => (
    <TableContainer sx={{ mt: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              {t('pages.pricing.be_pricing.table.operation', { defaultValue: 'Operation' })}
            </TableCell>
            <TableCell>
              {t('pages.pricing.distribution_type', { defaultValue: 'Distribution type' })}
            </TableCell>
            <TableCell>
              {t('pages.pricing.be_pricing.table.pricing', { defaultValue: 'Pricing' })}
            </TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.operationType} hover>
              <TableCell>
                {t(`pages.pricing.operation_type.${row.operationType?.toLowerCase()}`, {
                  defaultValue: row.operationType,
                })}
              </TableCell>
              <TableCell>
                {t(`pages.pricing.distribution_types.${row.distributionType}`, {
                  defaultValue: row.distributionType,
                })}
              </TableCell>
              <TableCell>{pricingChip(row.pricingType)}</TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() =>
                      openDetails(OperationActionEnum.EDIT, operationGroup, row.operationType)
                    }
                  >
                    {t('global.edit', { defaultValue: 'Edit' })}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={() =>
                      openDetails(OperationActionEnum.VIEW, operationGroup, row.operationType)
                    }
                  >
                    {t('global.view', { defaultValue: 'View' })}
                  </Button>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const tabLabel = (key: TabKey, customCount: number, totalCount: number) => (
    <Stack alignItems="center" spacing={0.5}>
      <span>
        {t(`pages.pricing.be_pricing.operations.${key}`, { defaultValue: key })}
      </span>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Chip
          size="small"
          color={customCount > 0 ? 'warning' : 'default'}
          label={
            customCount > 0
              ? t(`pages.pricing.be_pricing.table.labels.CUSTOM`, { defaultValue: 'Custom' })
              : t(`pages.pricing.be_pricing.table.labels.DEFAULT`, { defaultValue: 'Default' })
          }
        />
        {customCount > 0 && (
          <Typography variant="caption">
            {customCount} {t('PAGINATOR.OF_LABEL', { defaultValue: 'of' })} {totalCount}
          </Typography>
        )}
      </Stack>
    </Stack>
  );

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t('menus.businessEntitiesPricingOverview', {
          defaultValue: 'Business Entity Pricing Overview',
        })}
      </Typography>

      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="h6">{be?.companyName ?? 'N/A'}</Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<BusinessIcon />}
            onClick={() => navigate('/pricing/be')}
          >
            {t('global.buttons.back_to_business_entities', {
              defaultValue: 'Back to Business Entities',
            })}
          </Button>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* Subscription type row */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('pages.pricing.subscription_type', { defaultValue: 'Subscription type:' })}
          </Typography>
          <Typography variant="body2">
            {be?.subscriptionTier
              ? t(`pages.pricing.subscription_types.${be.subscriptionTier}`, {
                  defaultValue: be.subscriptionTier,
                })
              : 'N/A'}
          </Typography>
        </Stack>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Tabs value={selectedTabIdx} onChange={handleTabChange} sx={{ mb: 1 }}>
              <Tab
                label={tabLabel('internal-fees', customFeeCount, feeOps.length)}
              />
              <Tab
                label={tabLabel('internal-revenues', customRevenueCount, revenueOps.length)}
              />
            </Tabs>

            {selectedTab === 'internal-fees' &&
              renderOpsTable(feeOps, TAB_GROUPS['internal-fees'])}
            {selectedTab === 'internal-revenues' &&
              renderOpsTable(revenueOps, TAB_GROUPS['internal-revenues'])}
          </>
        )}
      </Paper>
    </Box>
  );
}
